import React, { useMemo, useState } from 'react';

const CompetitiveBenchmarking = ({ category, reviews, allCategories }) => {
  const [timeRange, setTimeRange] = useState('overall'); // 'overall', 'recent', 'quarterly'
  
  // Get benchmark data for this category compared to others
  const benchmarkData = useMemo(() => {
    if (!reviews || !reviews.length || !category || !allCategories) {
      return { category: null, related: [], industryAvg: null };
    }
    
    // Filter reviews by time range
    let filteredReviews = [...reviews];
    
    if (timeRange === 'recent') {
      // Last 30 days
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filteredReviews = filteredReviews.filter(review => new Date(review.date) >= thirtyDaysAgo);
    } else if (timeRange === 'quarterly') {
      // Last quarter (3 months)
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      filteredReviews = filteredReviews.filter(review => new Date(review.date) >= threeMonthsAgo);
    }
    
    // Calculate sentiment stats for current category
    const categoryReviews = filteredReviews.filter(review =>
      review.categorySentiments.some(cs => cs.category === category)
    );
    
    // Related categories (in real app, this would use category taxonomy or ML to find similar categories)
    // For demo, we'll use a predefined map
    const relatedCategoriesMap = {
      'Product Quality': ['Product Effectiveness', 'Packaging', 'Durability'],
      'Customer Service': ['Response Time', 'Problem Resolution', 'Staff Knowledge'],
      'Delivery & Shipping': ['Delivery Speed', 'Packaging', 'Tracking Updates'],
      'App Experience': ['Ease of Use', 'App Performance', 'UI Design'],
      'Price & Value': ['Discounts', 'Promotions', 'Competitive Pricing']
    };
    
    // Default to some categories if no predefined mapping exists
    const relatedCategoryNames = relatedCategoriesMap[category] || 
      allCategories.filter(c => c !== category).slice(0, 3);
    
    // Get available categories from data
    const availableCategories = new Set();
    filteredReviews.forEach(review => {
      review.categorySentiments.forEach(cs => {
        availableCategories.add(cs.category);
      });
    });
    
    // Filter related categories to only include available ones
    const availableRelatedCategories = relatedCategoryNames.filter(c => 
      availableCategories.has(c)
    );
    
    // Calculate stats for each category
    const calculateCategoryStats = (categoryName) => {
      const relevantReviews = filteredReviews.filter(review =>
        review.categorySentiments.some(cs => cs.category === categoryName)
      );
      
      if (relevantReviews.length === 0) {
        return null;
      }
      
      const positiveSentiments = relevantReviews.filter(review =>
        review.categorySentiments.find(cs => cs.category === categoryName)?.sentiment === 'Positive'
      ).length;
      
      const negativeSentiments = relevantReviews.filter(review =>
        review.categorySentiments.find(cs => cs.category === categoryName)?.sentiment === 'Negative'
      ).length;
      
      return {
        name: categoryName,
        total: relevantReviews.length,
        positive: positiveSentiments,
        negative: negativeSentiments,
        neutral: relevantReviews.length - positiveSentiments - negativeSentiments,
        positivePercentage: Math.round((positiveSentiments / relevantReviews.length) * 100),
        negativePercentage: Math.round((negativeSentiments / relevantReviews.length) * 100)
      };
    };
    
    // Get stats for current category
    const currentCategoryStats = calculateCategoryStats(category);
    
    // Get stats for related categories
    const relatedCategoriesStats = availableRelatedCategories
      .map(categoryName => calculateCategoryStats(categoryName))
      .filter(stats => stats !== null);
    
    // Calculate industry average (all categories)
    const allCategoriesStats = Array.from(availableCategories)
      .map(categoryName => calculateCategoryStats(categoryName))
      .filter(stats => stats !== null);
    
    const industryAvg = {
      total: allCategoriesStats.reduce((sum, stats) => sum + stats.total, 0),
      positive: allCategoriesStats.reduce((sum, stats) => sum + stats.positive, 0),
      neutral: allCategoriesStats.reduce((sum, stats) => sum + stats.neutral, 0),
      negative: allCategoriesStats.reduce((sum, stats) => sum + stats.negative, 0)
    };
    
    industryAvg.positivePercentage = Math.round(
      (industryAvg.positive / industryAvg.total) * 100
    );
    industryAvg.negativePercentage = Math.round(
      (industryAvg.negative / industryAvg.total) * 100
    );
    
    return {
      category: currentCategoryStats,
      related: relatedCategoriesStats,
      industryAvg
    };
  }, [reviews, category, allCategories, timeRange]);
  
  // Metrics to display in the comparison
  const metrics = [
    { key: 'positivePercentage', label: 'Positive Sentiment', type: 'percentage', higherIsBetter: true },
    { key: 'negativePercentage', label: 'Negative Sentiment', type: 'percentage', higherIsBetter: false },
    { key: 'total', label: 'Total Mentions', type: 'count', higherIsBetter: null }
  ];
  
  // Get bar width based on value and maximum
  const getBarWidth = (value, maximum) => {
    return `${(value / maximum) * 100}%`;
  };
  
  // Get color class based on performance comparison
  const getComparisonColorClass = (current, benchmark, higherIsBetter) => {
    if (higherIsBetter === null) return 'text-gray-700'; // Neutral for metrics like count
    
    const diff = current - benchmark;
    if (Math.abs(diff) < 3) return 'text-gray-700'; // Roughly equal (within 3%)
    
    if (higherIsBetter) {
      return diff > 0 ? 'text-green-600' : 'text-red-600';
    } else {
      return diff < 0 ? 'text-green-600' : 'text-red-600';
    }
  };
  
  // Get arrow and text for performance comparison
  const getPerformanceIndicator = (current, benchmark, higherIsBetter) => {
    if (higherIsBetter === null) return null; // No indicator for neutral metrics
    
    const diff = current - benchmark;
    if (Math.abs(diff) < 3) {
      return { arrow: '⟷', class: 'text-gray-700', text: 'On par' };
    }
    
    let isPositive;
    if (higherIsBetter) {
      isPositive = diff > 0;
    } else {
      isPositive = diff < 0;
    }
    
    return {
      arrow: isPositive ? '↑' : '↓',
      class: isPositive ? 'text-green-600' : 'text-red-600',
      text: isPositive ? 'Better' : 'Worse'
    };
  };
  
  // Get text descriptions for insights
  const getInsights = () => {
    if (!benchmarkData.category) return [];
    
    const insights = [];
    
    // Positive sentiment comparison with industry average
    const positiveDiff = benchmarkData.category.positivePercentage - benchmarkData.industryAvg.positivePercentage;
    if (Math.abs(positiveDiff) >= 5) {
      if (positiveDiff > 0) {
        insights.push(`${category} is performing ${positiveDiff}% better than the industry average for positive sentiment.`);
      } else {
        insights.push(`${category} is performing ${Math.abs(positiveDiff)}% worse than the industry average for positive sentiment.`);
      }
    } else {
      insights.push(`${category} is performing on par with the industry average for positive sentiment.`);
    }
    
    // Comparison with related categories
    if (benchmarkData.related.length > 0) {
      const topPerformer = benchmarkData.related.reduce((best, current) => 
        current.positivePercentage > best.positivePercentage ? current : best, 
        { name: '', positivePercentage: 0 }
      );
      
      if (topPerformer.positivePercentage > benchmarkData.category.positivePercentage + 5) {
        insights.push(`${topPerformer.name} is outperforming ${category} by ${topPerformer.positivePercentage - benchmarkData.category.positivePercentage}% in positive sentiment.`);
      }
      
      const worstPerformer = benchmarkData.related.reduce((worst, current) => 
        current.positivePercentage < worst.positivePercentage ? current : worst, 
        { name: '', positivePercentage: 100 }
      );
      
      if (benchmarkData.category.positivePercentage > worstPerformer.positivePercentage + 5) {
        insights.push(`${category} is outperforming ${worstPerformer.name} by ${benchmarkData.category.positivePercentage - worstPerformer.positivePercentage}% in positive sentiment.`);
      }
    }
    
    // Mention volume insights
    const totalMentionsDiff = benchmarkData.category.total - (benchmarkData.industryAvg.total / (benchmarkData.related.length + 1));
    const avgMentions = Math.round(benchmarkData.industryAvg.total / (benchmarkData.related.length + 1));
    
    if (totalMentionsDiff > avgMentions * 0.2) {
      insights.push(`${category} has higher mention volume (${benchmarkData.category.total}) compared to the category average (${avgMentions}).`);
    } else if (totalMentionsDiff < -avgMentions * 0.2) {
      insights.push(`${category} has lower mention volume (${benchmarkData.category.total}) compared to the category average (${avgMentions}).`);
    }
    
    return insights;
  };
  
  // Main render
  return (
    <div className="">
      <div className="flex flex-wrap justify-between items-center mb-4">
        <h3 className="text-md font-medium text-gray-900">Competitive Benchmark: {category}</h3>
        
        {/* Time range selector */}
        <div className="flex bg-gray-100 p-1 rounded-md">
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              timeRange === 'overall'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setTimeRange('overall')}
          >
            All Time
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              timeRange === 'quarterly'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setTimeRange('quarterly')}
          >
            Last Quarter
          </button>
          <button
            className={`px-3 py-1 text-xs rounded-md ${
              timeRange === 'recent'
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => setTimeRange('recent')}
          >
            Last 30 Days
          </button>
        </div>
      </div>
      
      {/* No data state */}
      {!benchmarkData.category && (
        <div className="bg-gray-50 p-5 rounded-lg text-center">
          <p className="text-gray-500">No data available for benchmarking this category.</p>
        </div>
      )}
      
      {/* Benchmarking data */}
      {benchmarkData.category && (
        <>
          {/* Insights Panel */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5">
            <h4 className="text-sm font-medium text-blue-800 mb-2">Key Insights</h4>
            <ul className="space-y-1">
              {getInsights().map((insight, idx) => (
                <li key={idx} className="text-sm text-blue-700 flex">
                  <span className="mr-2">•</span>
                  <span>{insight}</span>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Comparison Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                    Metric
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {benchmarkData.category.name}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Industry Avg
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comparison
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Performance
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {metrics.map((metric, idx) => {
                  const currentValue = benchmarkData.category[metric.key];
                  const benchmarkValue = benchmarkData.industryAvg[metric.key];
                  const performance = getPerformanceIndicator(currentValue, benchmarkValue, metric.higherIsBetter);
                  
                  const maxValue = metric.type === 'percentage' ? 100 : 
                    Math.max(currentValue, benchmarkValue) * 1.2; // Add 20% padding for count metrics
                  
                  return (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {metric.label}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 h-2 rounded-full overflow-hidden mr-2">
                            <div 
                              className={`h-full ${
                                metric.key === 'positivePercentage' ? 'bg-green-500' :
                                metric.key === 'negativePercentage' ? 'bg-red-500' :
                                'bg-blue-500'
                              }`}
                              style={{ width: getBarWidth(currentValue, maxValue) }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">
                            {currentValue}{metric.type === 'percentage' ? '%' : ''}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-32 bg-gray-200 h-2 rounded-full overflow-hidden mr-2">
                            <div 
                              className={`h-full ${
                                metric.key === 'positivePercentage' ? 'bg-green-300' :
                                metric.key === 'negativePercentage' ? 'bg-red-300' :
                                'bg-blue-300'
                              }`}
                              style={{ width: getBarWidth(benchmarkValue, maxValue) }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-600">
                            {benchmarkValue}{metric.type === 'percentage' ? '%' : ''}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${getComparisonColorClass(currentValue, benchmarkValue, metric.higherIsBetter)}`}>
                          {currentValue > benchmarkValue ? '+' : ''}
                          {currentValue - benchmarkValue}
                          {metric.type === 'percentage' ? '%' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {performance && (
                          <span className={`text-sm font-medium ${performance.class}`}>
                            {performance.arrow} {performance.text}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {/* Related Categories Comparison */}
          {benchmarkData.related.length > 0 && (
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Comparison with Related Categories</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Positive Sentiment Comparison */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Positive Sentiment Comparison</h5>
                  <div className="space-y-3">
                    {/* Current category */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs font-medium">{benchmarkData.category.name}</span>
                        <span className="text-xs">{benchmarkData.category.positivePercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500" 
                          style={{ width: `${benchmarkData.category.positivePercentage}%` }}
                        ></div>
                      </div>
                    </div>
                    
                    {/* Related categories */}
                    {benchmarkData.related.map((relatedCat, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between mb-1">
                          <span className="text-xs">{relatedCat.name}</span>
                          <span className="text-xs">{relatedCat.positivePercentage}%</span>
                        </div>
                        <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-green-300" 
                            style={{ width: `${relatedCat.positivePercentage}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Industry average */}
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-xs italic">Industry Average</span>
                        <span className="text-xs">{benchmarkData.industryAvg.positivePercentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-400" 
                          style={{ width: `${benchmarkData.industryAvg.positivePercentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Mention Volume Comparison */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h5 className="text-xs font-medium text-gray-600 mb-2">Mention Volume Comparison</h5>
                  <div className="space-y-3">
                    {/* Calculate max value for scaling */}
                    {(() => {
                      const allValues = [
                        benchmarkData.category.total,
                        ...benchmarkData.related.map(r => r.total),
                        Math.round(benchmarkData.industryAvg.total / (benchmarkData.related.length + 1))
                      ];
                      const maxValue = Math.max(...allValues);
                      
                      return (
                        <>
                          {/* Current category */}
                          <div>
                            <div className="flex justify-between mb-1">
                              <span className="text-xs font-medium">{benchmarkData.category.name}</span>
                              <span className="text-xs">{benchmarkData.category.total}</span>
                            </div>
                            <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-blue-500" 
                                style={{ width: getBarWidth(benchmarkData.category.total, maxValue) }}
                              ></div>
                            </div>
                          </div>
                          
                          {/* Related categories */}
                          {benchmarkData.related.map((relatedCat, idx) => (
                            <div key={idx}>
                              <div className="flex justify-between mb-1">
                                <span className="text-xs">{relatedCat.name}</span>
                                <span className="text-xs">{relatedCat.total}</span>
                              </div>
                              <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-blue-300" 
                                  style={{ width: getBarWidth(relatedCat.total, maxValue) }}
                                ></div>
                              </div>
                            </div>
                          ))}
                          
                          {/* Category average */}
                          {(() => {
                            const avgMentions = Math.round(benchmarkData.industryAvg.total / (benchmarkData.related.length + 1));
                            return (
                              <div>
                                <div className="flex justify-between mb-1">
                                  <span className="text-xs italic">Category Average</span>
                                  <span className="text-xs">{avgMentions}</span>
                                </div>
                                <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-gray-400" 
                                    style={{ width: getBarWidth(avgMentions, maxValue) }}
                                  ></div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CompetitiveBenchmarking;